import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, Users, BookOpen, Target } from 'lucide-react';

interface APSMaturityLevel {
  level: string;
  workshops_count: number;
  completion_rate: number;
  average_progress: number;
  skill_development_score: number;
  knowledge_transfer_rate: number;
}

interface APSMaturity {
  current_maturity_distribution: APSMaturityLevel[];
  progression_tracking: Array<{
    period: string;
    beginner_count: number;
    intermediate_count: number;
    advanced_count: number;
    expert_count: number;
  }>;
  skill_development_metrics: {
    total_skills_developed: number;
    average_skill_level: number;
    skill_development_rate: number;
    top_skills: string[];
  };
  knowledge_transfer_metrics: {
    knowledge_sessions_conducted: number;
    knowledge_retention_rate: number;
    cross_team_learning: number;
    mentor_effectiveness: number;
  };
  maturity_improvement_rate: number;
  recommended_learning_paths: Array<{
    path_name: string;
    target_audience: string;
    duration: string;
    skills_covered: string[];
    completion_rate: number;
  }>;
}

interface MaturityProgressionChartProps {
  data: APSMaturity;
  title?: string;
}

const MaturityProgressionChart: React.FC<MaturityProgressionChartProps> = ({ 
  data, 
  title = "APS Maturity Progression" 
}) => {
  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner': return '#ef4444';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#3b82f6';
      case 'expert': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner': return '🌱';
      case 'intermediate': return '🌿';
      case 'advanced': return '🌳';
      case 'expert': return '🏆';
      default: return '📊';
    }
  };

  // Prepare data for progression chart
  const progressionData = data.progression_tracking.map(period => ({
    period: period.period,
    Beginner: period.beginner_count,
    Intermediate: period.intermediate_count,
    Advanced: period.advanced_count,
    Expert: period.expert_count,
    total: period.beginner_count + period.intermediate_count + period.advanced_count + period.expert_count
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{`Period: ${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          <span className="text-sm text-gray-600">Maturity Tracking</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Current Maturity Distribution */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Current Maturity Distribution</h4>
          <div className="space-y-3">
            {data.current_maturity_distribution.map((level) => {
              const color = getLevelColor(level.level);
              const icon = getLevelIcon(level.level);
              
              return (
                <div key={level.level} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{icon}</span>
                      <span className="font-semibold text-gray-900">{level.level}</span>
                    </div>
                    <span className="text-sm font-semibold" style={{ color }}>
                      {level.workshops_count} workshops
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Completion Rate</span>
                      <span className="text-sm font-semibold text-green-600">
                        {level.completion_rate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Average Progress</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {level.average_progress.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Skill Development</span>
                      <span className="text-sm font-semibold text-purple-600">
                        {level.skill_development_score.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Knowledge Transfer</span>
                      <span className="text-sm font-semibold text-orange-600">
                        {level.knowledge_transfer_rate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progression Tracking Chart */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Maturity Progression Over Time</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={progressionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="Beginner" stackId="a" fill="#ef4444" />
                <Bar dataKey="Intermediate" stackId="a" fill="#f59e0b" />
                <Bar dataKey="Advanced" stackId="a" fill="#3b82f6" />
                <Bar dataKey="Expert" stackId="a" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Skill Development Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <h4 className="font-semibold text-blue-900">Skill Development</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-700">Total Skills Developed</span>
              <span className="text-sm font-semibold text-blue-900">
                {data.skill_development_metrics.total_skills_developed}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-700">Average Skill Level</span>
              <span className="text-sm font-semibold text-blue-900">
                {data.skill_development_metrics.average_skill_level.toFixed(1)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-700">Development Rate</span>
              <span className="text-sm font-semibold text-blue-900">
                {data.skill_development_metrics.skill_development_rate.toFixed(1)}%
              </span>
            </div>
            <div className="mt-3">
              <p className="text-sm text-blue-700 mb-2">Top Skills:</p>
              <div className="flex flex-wrap gap-1">
                {data.skill_development_metrics.top_skills.map((skill, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-green-500" />
            <h4 className="font-semibold text-green-900">Knowledge Transfer</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-700">Sessions Conducted</span>
              <span className="text-sm font-semibold text-green-900">
                {data.knowledge_transfer_metrics.knowledge_sessions_conducted}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-700">Retention Rate</span>
              <span className="text-sm font-semibold text-green-900">
                {data.knowledge_transfer_metrics.knowledge_retention_rate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-700">Cross-team Learning</span>
              <span className="text-sm font-semibold text-green-900">
                {data.knowledge_transfer_metrics.cross_team_learning}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-700">Mentor Effectiveness</span>
              <span className="text-sm font-semibold text-green-900">
                {data.knowledge_transfer_metrics.mentor_effectiveness.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Improvement Rate and Learning Paths */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-purple-500" />
            <h4 className="font-semibold text-purple-900">Maturity Improvement</h4>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {data.maturity_improvement_rate.toFixed(1)}%
            </div>
            <p className="text-sm text-purple-700">Improvement Rate</p>
            <div className="mt-3">
              <div className="w-full bg-purple-200 rounded-full h-3">
                <div 
                  className="bg-purple-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(data.maturity_improvement_rate, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Recommended Learning Paths</h4>
          <div className="space-y-3">
            {data.recommended_learning_paths.map((path, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-semibold text-gray-900">{path.path_name}</h5>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {path.duration}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">Target: {path.target_audience}</p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {path.skills_covered.map((skill, skillIndex) => (
                    <span key={skillIndex} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Completion Rate</span>
                  <span className="text-sm font-semibold text-green-600">
                    {path.completion_rate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaturityProgressionChart;


